/** Date/time conversion: user input (date + time + timezone) ↔ UTC ISO 8601. */

import { isPast, isValid } from 'date-fns'
import { fromZonedTime } from 'date-fns-tz'
import { getUserTimezone } from './timezone'

/** Local date (YYYY-MM-DD) + time (HH:mm) in given IANA timezone → UTC ISO string. Uses fromZonedTime so the string is interpreted in that zone, not the runtime. */
export function convertLocalToUTC(
  date: string,
  time: string,
  timezone: string
): string | undefined {
  if (!date || !time) return undefined

  const localInZone = `${date}T${time}:00`
  const utcDate = fromZonedTime(localInZone, timezone)
  return utcDate.toISOString()
}

/** UTC ISO string → local date (YYYY-MM-DD) and time (HH:mm) in given timezone. Uses formatToParts so output is always YYYY-MM-DD/HH:mm regardless of locale. */
export function parseUTCToLocal(
  isoString: string | undefined,
  timezone: string
): { date: string; time: string } | null {
  if (!isoString) return null

  const date = new Date(isoString)
  if (Number.isNaN(date.getTime())) return null

  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
  const parts = formatter.formatToParts(date)
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? ''

  const year = get('year')
  const month = get('month')
  const day = get('day')
  const hour = get('hour')
  const minute = get('minute')

  if (!year || !month || !day || !hour || !minute) return null

  const dateStr = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
  const timeStr = `${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`

  return { date: dateStr, time: timeStr }
}

export function prepareDateForApi(
  date: Date,
  timezone: string = getUserTimezone()
): { eventDate: string; eventTimezone: string } {
  return {
    eventDate: date.toISOString(),
    eventTimezone: timezone,
  }
}

export function validateDateInput(date: Date): {
  isValid: boolean
  error?: string
} {
  if (!isValid(date)) {
    return { isValid: false, error: 'Invalid date' }
  }
  if (isPast(date)) {
    return { isValid: false, error: 'Date cannot be in the past' }
  }
  return { isValid: true }
}
